import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { RichText } from "@/components/RichText";
import { readingTime } from "@/lib/readingTime";
import api from "@/lib/api";
import { usePageTitle } from "@/lib/usePageTitle";

const TYPE_LABELS = {
  note: "Research Note",
  paper: "Position Paper",
  observatory: "Observatory Signal",
  publication: "Publication",
};

export default function ResearchDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  usePageTitle(item ? `${item.title} | Mentheris` : "Research | Mentheris", item?.excerpt);

  useEffect(() => {
    api
      .get(`/research/${id}`)
      .then(({ data }) => setItem(data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <main className="pt-40 pb-32 mx-auto max-w-[1400px] px-6 md:px-12" data-testid="research-detail-not-found">
        <p className="font-serif text-3xl">This entry could not be found.</p>
        <Link to="/research" className="link-editorial text-sm mt-6 inline-block">Back to Research</Link>
      </main>
    );

  if (!item)
    return (
      <main className="pt-40 pb-32 mx-auto max-w-[1400px] px-6 md:px-12" data-testid="research-detail-loading">
        <p className="text-fog text-sm">Loading…</p>
      </main>
    );

  const TYPE_LABEL = TYPE_LABELS[item.type] || item.type;

  return (
    <main className="pt-24 md:pt-32 pb-24" data-testid="research-detail-page">
      <article className="mx-auto max-w-[900px] px-6 md:px-12">
        <Reveal>
          <Link to="/research" data-testid="detail-back-link" className="group inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-fog hover:text-ink transition-colors duration-300">
            <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Research
          </Link>
        </Reveal>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <span className="text-[10px] tracking-[0.2em] uppercase border border-line px-3 py-1 text-fog" data-testid="detail-type-badge">
            {TYPE_LABELS[item.type] || item.type}
          </span>
          {item.date && <span className="text-xs tracking-[0.2em] uppercase text-fog" data-testid="detail-date">{item.date}</span>}
          <span className="text-xs tracking-[0.2em] uppercase text-fog" data-testid="detail-reading-time">
            {readingTime(item.body || item.excerpt)} min read
          </span>
        </div>

        <h1 className="font-serif font-medium tracking-tight leading-[1.1] text-4xl md:text-6xl mt-8" data-testid="detail-title">
          <MaskedLine delay={0.15}>{item.title}</MaskedLine>
        </h1>

        <Reveal delay={0.3} className="mt-12 border-t border-line pt-12">
          {item.excerpt && (
            <p className="font-serif italic text-xl md:text-2xl leading-relaxed text-ink/80 mb-10" data-testid="detail-excerpt">
              {item.excerpt}
            </p>
          )}
          <div data-testid="detail-body">
            <RichText text={item.body || ""} />
          </div>

          {item.link && (
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="detail-external-link"
              className="group inline-flex items-center gap-3 mt-12 border border-ink px-8 py-4 text-sm tracking-wide hover:bg-ink hover:text-bone transition-colors duration-300"
            >
              Read the full publication
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          )}

          <div className="flex flex-wrap gap-2 mt-10">
            {item.tags?.map((t) => (
              <span key={t} className="text-[10px] tracking-[0.15em] uppercase border border-line px-3 py-1 text-fog">{t}</span>
            ))}
          </div>
        </Reveal>
      </article>
    </main>
  );
}
